import { RecordingSpeakerRoleType } from "@repo/app-types/entities/recording";
import {
  IconPlayerPause,
  IconPlayerPlay,
  IconUser,
  IconVolume,
} from "@tabler/icons-react";
import { Duration } from "luxon";
import type { FC, ReactNode } from "react";
import { Button as AriaButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { useCapturedPlayPauseAction } from "./useCapturedPlayPauseAction.js";

export interface TranscriptMessagePlayback {
  /** The start time of this message, in seconds. */
  time: number;
  /** Whether the playhead is within this message, including while paused. */
  isCurrent: boolean;
  isPlaying: boolean;
  /** Plays from the start of this message. */
  onPlay: () => void;
  onPause: () => void;
}

export interface TranscriptMessageProps {
  speakerName: string;
  speakerRole?: RecordingSpeakerRoleType | undefined;
  /** Omit for a message without audio, such as a chat message. */
  playback?: TranscriptMessagePlayback | undefined;
  children: ReactNode;
}

export const TranscriptMessage: FC<TranscriptMessageProps> = (props) => {
  const { speakerName, speakerRole, playback, children } = props;
  const isUnknownSpeaker = speakerName === "Unknown speaker";

  const captured = useCapturedPlayPauseAction(playback?.isPlaying ?? false);
  // The current message follows live playback even if hover or focus captured
  // an earlier action.
  const action = playback?.isCurrent
    ? playback.isPlaying
      ? "pause"
      : "play"
    : captured.action;

  const avatar = (
    <div
      aria-hidden="true"
      className={twMerge(
        "flex size-8 shrink-0 items-center justify-center rounded-md text-xs font-medium",
        speakerRole === RecordingSpeakerRoleType.Customer &&
          "bg-amber-50 text-amber-900",
        speakerRole === RecordingSpeakerRoleType.Representative &&
          "bg-blue-50 text-blue-800",
        (speakerRole === undefined ||
          speakerRole === RecordingSpeakerRoleType.Other) &&
          "bg-gray-100 text-gray-600",
      )}
    >
      {isUnknownSpeaker ? (
        <IconUser className="size-4" />
      ) : (
        buildSpeakerInitials(speakerName)
      )}
    </div>
  );

  const content = (
    <div className="min-w-0 flex-1">
      <span
        className={twMerge(
          "block truncate text-xs font-semibold",
          playback && "pr-24",
        )}
      >
        {speakerName}
      </span>
      <div className="mt-1 space-y-2 text-sm leading-relaxed">{children}</div>
    </div>
  );

  if (!playback) {
    return (
      <div data-transcript-message className="-ml-2 flex items-start gap-1">
        <div className="mt-2 ml-2">{avatar}</div>
        <div className="min-w-0 flex-1 rounded-lg px-2 py-2">{content}</div>
      </div>
    );
  }

  const formattedTime = Duration.fromMillis(playback.time * 1000).toFormat(
    "h:mm:ss",
  );
  const PlayOrPauseIcon = action === "play" ? IconPlayerPlay : IconPlayerPause;

  return (
    <div data-transcript-message className="-ml-2 flex items-start gap-1">
      <div className="mt-2 ml-2">{avatar}</div>
      <div
        aria-current={playback.isCurrent ? "true" : undefined}
        className={twMerge(
          "group/message relative min-w-0 flex-1 rounded-lg transition-[background-color]",
          playback.isCurrent && "bg-gray-100",
        )}
      >
        <div className="rounded-lg px-2 py-2">{content}</div>
        <AriaButton
          className="absolute top-2 right-2 flex items-center gap-1"
          aria-label={
            action === "play" ? `Play from ${formattedTime}` : "Pause"
          }
          {...captured.captureProps}
          onPress={() => {
            if (action === "play") {
              playback.onPlay();
            } else {
              playback.onPause();
            }
            captured.onActionPerformed(action);
          }}
        >
          <div
            className={twMerge(
              "size-4 opacity-0 transition-opacity group-focus-within/message:opacity-100 group-hover/message:opacity-100",
              playback.isCurrent && "opacity-100 transition-none",
            )}
          >
            {playback.isPlaying ? (
              <IconVolume className="size-full text-indigo-500" />
            ) : (
              <PlayOrPauseIcon className="size-full text-muted-foreground" />
            )}
          </div>
          <span className="font-mono text-xs text-muted-foreground select-none">
            {formattedTime}
          </span>
        </AriaButton>
      </div>
    </div>
  );
};

function buildSpeakerInitials(speakerName: string): string {
  const words = speakerName.trim().split(/\s+/);
  const firstInitial = words[0]?.charAt(0) ?? "";
  const lastInitial = words.length > 1 ? (words.at(-1)?.charAt(0) ?? "") : "";

  return `${firstInitial}${lastInitial}`.toLocaleUpperCase();
}
